import { DataStatus, Message } from 'src/core/engine/types';
import { errorMessage, successMessage } from 'src/util/state-helpers';
import { useDeps } from 'src/hooks/useDeps';
import { useConstant } from 'src/core/hooks/react-hooks';
import { useCallback, useEffect, useReducer } from 'react';
import { EMPTY, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs';
import { execute } from 'swagger/ts/WorkwearExpressQuickQuoteContactServiceV1ContactPost';
import { extractErrorAndCode } from 'src/util/errors';

/**
 * Wrapper for the contact form side-effects
 */

export type ProductQuickQuoteState = {
    status: DataStatus;
    messages: Message[];
};

const initialState: ProductQuickQuoteState = {
    status: DataStatus.Idle,
    messages: [],
};

export type ContactFormData = {
    email: string;
    message: string;
    name: string;
    telephone: string;
    category?: string;
};

type ProductQuickQuoteMsg =
    | {
          kind: 'submit';
          contactData: ContactFormData;
          category?: string;
          sku: string;
      }
    | {
          kind: 'error' | 'success';
          message: string;
      };

function contactReducer(state = initialState, action: ProductQuickQuoteMsg): ProductQuickQuoteState {
    switch (action.kind) {
        case 'submit': {
            return {
                ...state,
                status: DataStatus.Pending,
                messages: [],
            };
        }
        case 'error': {
            return {
                ...state,
                status: DataStatus.Error,
                messages: [errorMessage(action.message)],
            };
        }
        case 'success': {
            return {
                ...state,
                status: DataStatus.Success,
                messages: [successMessage(action.message)],
            };
        }
        default:
            return state;
    }
}

export function useQuickQuote() {
    const deps = useDeps();
    const [state, dispatch] = useReducer(contactReducer, initialState);

    const mirror$ = useConstant(() => new Subject<ProductQuickQuoteMsg>());

    useEffect(() => {
        const sub = mirror$
            .pipe(
                filter((x) => x.kind === 'submit'),
                switchMap((msg) => {
                    if (msg.kind === 'submit') {
                        const ajax$ = execute(
                            {
                                contactData: {
                                    email: msg.contactData.email,
                                    name: msg.contactData.name,
                                    telephone: msg.contactData.telephone,
                                    message: msg.contactData.message,
                                    sku: msg.sku,
                                    category: msg.category,
                                },
                            },
                            deps,
                        );
                        return ajax$.pipe(
                            map(() => ({ kind: 'success', message: 'Sent!' })),
                            catchError((err) => {
                                const { error } = extractErrorAndCode(err);
                                return of({ kind: 'error', message: error });
                            }),
                        );
                    }
                    return EMPTY;
                }),
                tap((msg) => dispatch(msg as ProductQuickQuoteMsg)),
            )
            .subscribe();
        return () => {
            sub.unsubscribe();
        };
    }, [deps, mirror$]);

    const submit = useCallback(
        (contactData: ContactFormData, sku: string, category?: string) => {
            const msg = { kind: 'submit' as const, contactData, sku, category };
            dispatch(msg);
            mirror$.next(msg);
        },
        [mirror$],
    );

    return {
        state,
        submit,
    };
}
